import { CommonModule } from '@angular/common';
import { Component, computed, inject, Signal } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ModalService } from '@app/_services/modal.service';
import { DeletedEntity } from '@app/_interfaces/feature.interface';
import { Todo } from '@app/_interfaces/todo.interface';
import { ParseRulePipe } from '@app/_pipes/parse-rule.pipe';
import { AlertService } from '@app/_services/alert.service';
import { SettingsService } from '@app/_services/settings.service';
import { UtilsService } from '@app/_services/utils.service';
import { TodoStore, ViewSettingsStore } from '@app/_stores';
import { SvgComponent } from '@app/components/shared/svg/svg.component';
import { TodoEntitiesComponent } from '../todo-entities/todo-entities.component';
import { TodoBlockComponent } from '../todo-block/todo-block.component';
import { CacheService } from '@app/_services/cache.service';
import { DependencyService } from '@app/_services/dependency.service';
import { TodoFormComponent } from '../todo-form/todo-form.component';

@Component({
  selector: 'app-todo-trash-tab',
  imports: [
    CommonModule,
    SvgComponent,
    TodoBlockComponent
  ],
  templateUrl: './todo-trash-tab.component.html',
  styleUrl: './todo-trash-tab.component.scss'
})

export class TodoTrashTabComponent {

  readonly todoStore = inject(TodoStore);
  // Show ALL metadata in trash screen (description is controlled by descriptionType, not show array)
  show: string[] = ['label', 'time', 'reminder', 'duration', 'attachments', 'repeat', 'checklist', 'collaboratorsCount', 'hashtag', 'deletedAt'];

  deletedTodos: Signal<DeletedEntity[]> = computed(() => {
    const deletedEntities: DeletedEntity[] = [];
    this.todoStore.todos().forEach(todo => {
      if (todo.deletedAt && !todo.permaDeletedAt && todo.uid === this.cc.user.uid) {
        deletedEntities.push({
          entityName: 'todo',
          entityId: todo.id,
          deletedAt: this.utils.toDate(todo.deletedAt),
          entries: this.ds.getAllDatesOfEntity(todo, 'todo', this.cc.user.uid),
          entityEntry: this.todoStore.getTodoEntry(todo.id),
          isRepeated: !!todo.repeat
        });
      }
    });

    deletedEntities.sort((a, b) => {
      return b.deletedAt.getTime() - a.deletedAt.getTime();
    });
    return deletedEntities;
  });
  idToTodo: Signal<Record<string, Todo>> = this.todoStore.idToTodo;
  viewSettingsStore = inject(ViewSettingsStore)
  constructor(
    public ss: SettingsService,
    private alertService: AlertService,
    public dialog: MatDialog,
    private modalService: ModalService,
    public cc: CacheService,
    public ds: DependencyService,
    private utils: UtilsService,
  ) { }

  trackByEntity(index: number, entity: DeletedEntity) {
    return entity.entityId;
  };

  getTodo(entityId: string): Todo {
    return this.idToTodo()[entityId];
  };

  getCheckedRange(entityId: string) {
    const todo = this.getTodo(entityId);
    if (!todo.checkLists) {
      return '';
    } else {
      const totalItems = todo.checkLists.length;
      const doneItems = todo.checkLists.filter(item => item.isCompleted === true).length;

      if (!totalItems || totalItems === 0) {
        return this.ss.t('lists_listsSubContent');
      }

      return `${doneItems}/${totalItems}`;
    }
  };

  openTodoEntities(entityId: string, isRepeated: boolean) {
    if (isRepeated) {
      this.modalService.openLevel2(
        TodoEntitiesComponent,
        { entityId: entityId },
        { disableClose: false, autoFocus: false }
      );
    } else {
      const todo = this.todoStore.idToTodo()[entityId];
      this.modalService.openLevel1(
        TodoFormComponent,
        {
          mode: 'edit',
          value: todo,
          dateString: todo.startDate || null
        },
        { disableClose: true, autoFocus: false }
      );
    }
  };

  async removeTodo(entityId: string) {
    const res = await this.alertService.confirmRevamped({
      title: this.ss.t('removeConfirmation_title'),
      description: this.ss.t('removeConfirmation_content'),
      yesButtonText: this.ss.t('common_buttonRemove'),
      noButtonText: this.ss.t('common_buttonCancel'),
      yesButtonColor: 'color-11',
      noButtonColor: 'color-7',
    });
    if (!res) return;
    const todo = this.getTodo(entityId);
    todo.permaDeletedAt = new Date();
    this.todoStore.updateTodos([todo]);
  };

  async restoreTodo(entityId: string) {

    //check if the feature is enabled
    const isFeatureEnabled = this.viewSettingsStore.showTodoFeature();
    if (!isFeatureEnabled) {
      await this.alertService.alert(this.ss.t('featureTurnedOff_title'), this.ss.t('featureTurnedOff_content'), this.ss.t('common_ok'));
    }

    const res = await this.alertService.confirmRevamped({
      title: this.ss.t('restoreConfirmation_title'),
      description: this.ss.t('restoreConfirmation_content'),
      yesButtonText: this.ss.t('common_restore'),
      noButtonText: this.ss.t('common_buttonCancel'),
      yesButtonColor: 'color-35',
      noButtonColor: 'color-7',
    });
    if (!res) return;
    const todo = this.getTodo(entityId);
    todo.deletedAt = null;
    this.todoStore.updateTodos([todo]);
    const isShow = await this.alertService.toast('todoRestored_content', isFeatureEnabled ? 'common_show' : '', 'common_dismiss');
    if (isShow) {
      this.openTodoEntities(entityId, false);
    }
  };
}
